import { useCallback } from 'react'

import { Slice } from '@tiptap/pm/model'
import { EditorView } from '@tiptap/pm/view'
import { find } from 'linkifyjs'

import { useEmbed } from '@tribeplatform/react-sdk/hooks'
import { noop } from '@tribeplatform/react-ui-kit/hooks'

import { useChatContext } from '../ChatContext/ChatContext.js'
import { isImage } from '../utils/image.js'

export const useChatPasteHandler = () => {
  const { addAttachments, addEmbeds } = useChatContext()
  const { asyncQuery: embed } = useEmbed()

  const getParsedUrlFromText = useCallback((text: string) => {
    const url = text.trim()
    const matched = find(url)
      .filter(link => link.isLink)
      .map(link => ({
        text: link.value,
        index: link.start,
        data: link,
      }))
    if (matched.length > 0) {
      const [firstMatch] = matched
      const parsedUrl = firstMatch.data.href
      return parsedUrl
    }

    return null
  }, [])

  const handleLinkToEmbed = useCallback(
    async (textContent: string) => {
      const parsedUrl = getParsedUrlFromText(textContent)
      if (parsedUrl) {
        const embedResult = await embed({ url: parsedUrl })
        if (embedResult?.html) {
          return embedResult
        }
      }
      return null
    },
    [embed, getParsedUrlFromText],
  )

  const pasteHandler = useCallback(
    (_: EditorView, event: ClipboardEvent, slice: Slice) => {
      const items = Array.from(event.clipboardData?.items || [])
      const pastedFiles = items
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
      const imageFiles = pastedFiles.filter(isImage)
      const otherFiles = pastedFiles.filter(file => !isImage(file))

      addAttachments(imageFiles, 'image')
      addAttachments(otherFiles, 'file')

      const textNodesContent: string[] = []
      slice.content.forEach(node => {
        if (node.type.name === 'text') {
          textNodesContent.push(node.text)
        }
        if (node.type.name === 'paragraph') {
          node.content.forEach(child =>
            textNodesContent.push(child.textContent),
          )
        }
      })
      Promise.all(
        textNodesContent.filter(Boolean).map(node => handleLinkToEmbed(node)),
      )
        .then(embedResults => addEmbeds(embedResults.filter(Boolean)))
        .catch(noop)
    },
    [addAttachments, addEmbeds, handleLinkToEmbed],
  )

  return {
    pasteHandler,
  }
}
